import { useState } from "react";
import {
  Wrench,
  ClipboardCheck,
  GraduationCap,
  Users,
  FolderKanban,
  Cog,
  ArrowLeftRight,
} from "lucide-react";
import { Reveal, scrollToId } from "@/components/motion";
import { EditorialMarquee } from "@/components/EditorialMarquee";
import { RequestForm } from "@/components/RequestForm";
import { B } from "@/components/BrandText";
import { usePageTitle } from "@/hooks/usePageTitle";

const helpTypes = [
  { icon: Wrench, title: "Troubleshooting", text: "Recurring problems that need someone who's seen them before.", testId: "help-type-troubleshooting" },
  { icon: ClipboardCheck, title: "Evaluations & Assessments", text: "An experienced, independent look at a process, purchase, property or operation.", testId: "help-type-evaluations" },
  { icon: GraduationCap, title: "Training", text: "Practical, hands-on training for individuals or teams.", testId: "help-type-training" },
  { icon: Users, title: "Mentoring", text: "Guidance from someone who has already walked the path.", testId: "help-type-mentoring" },
  { icon: FolderKanban, title: "Defined Projects", text: "A specific piece of work with a clear beginning and end.", testId: "help-type-projects" },
  { icon: Cog, title: "Operational Problems", text: "Day-to-day issues in production, facilities, fleet, office or service operations.", testId: "help-type-operations" },
  { icon: ArrowLeftRight, title: "Knowledge Transfer", text: "Capturing what your most experienced people know before it walks out the door.", testId: "help-type-knowledge-transfer" },
];

const steps = [
  { n: "1", title: "Tell us what you're trying to solve.", text: "Describe the problem, project or need. You don't need to know exactly what type of advisor you need.", testId: "need-step-1" },
  { n: "2", title: "We determine what kind of experience could help.", text: "We look at the situation and identify the background that actually fits it.", testId: "need-step-2" },
  { n: "3", title: "We identify an advisor whose background fits the situation.", text: "We look in our network for a One More Shift Advisor with relevant, real-world experience.", testId: "need-step-3" },
  { n: "4", title: "We help define the engagement.", text: "Scope, expectations and logistics are made clear before anything begins.", testId: "need-step-4" },
  { n: "5", title: "The advisor puts their experience to work.", text: "You get the guidance, training, evaluation or troubleshooting you needed.", testId: "need-step-5" },
];

const examples = [
  { title: "A problem that keeps coming back", text: "A manufacturer has a quality issue no one can pin down. A veteran production advisor troubleshoots the root cause and shows the team how to prevent it.", testId: "example-troubleshooting" },
  { title: "Knowledge that's about to walk out the door", text: "Your most experienced employee is retiring. An advisor helps capture and transfer what they know before it's gone.", testId: "example-knowledge-transfer" },
  { title: "People who need real training", text: "New drivers, new supervisors, new staff. An experienced advisor builds and delivers practical, hands-on training.", testId: "example-training" },
  { title: "A decision you don't want to get wrong", text: "You're weighing a major equipment, process or property decision. An independent advisor evaluates it with decades of perspective.", testId: "example-evaluation" },
  { title: "An operation that isn't running right", text: "Scheduling, workflow, maintenance, customer service — an experienced operations advisor assesses what's actually happening and what to change.", testId: "example-operations" },
];

export default function FindExperience() {
  usePageTitle(
    "I Need Experience | Find an Advisor | One More Shift",
    "You don't always need another hire. One More Shift helps businesses and individuals south and southeast of Houston find experienced advisors for troubleshooting, evaluations, training, mentoring, defined projects and knowledge transfer."
  );
  const [done, setDone] = useState(false);

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="find-experience-confirmation">
        <h1 className="text-3xl md:text-4xl font-bold">Thank you.</h1>
        <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
          Thanks. We're currently building our founding local network. We'll review your request and let you know
          whether we believe we can identify relevant experience.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="find-experience-page">
      <section className="bg-background" data-testid="need-hero-section">
        <div className="max-w-4xl mx-auto px-6 py-20 md:py-28">
          <Reveal>
            <p className="text-secondary font-semibold text-sm tracking-[0.3em] uppercase">I need experience</p>
            <h1 className="mt-4 text-4xl md:text-6xl font-bold leading-tight" data-testid="find-experience-headline">
              You don't always need another hire.
            </h1>
            <p className="mt-5 text-2xl md:text-3xl font-serif font-bold text-primary leading-snug" data-testid="find-experience-subheadline">
              Sometimes you need someone who's already solved the problem you're dealing with.
            </p>
            <p className="mt-6 text-lg text-foreground leading-relaxed max-w-3xl" data-testid="find-experience-supporting">
              <B /> helps businesses and individuals locate relevant, real-world experience for a defined need —
              without adding an employee.
            </p>
          </Reveal>
        </div>
      </section>

      <EditorialMarquee
        phrases={[
          "Tell us the problem.",
          "We find the experience.",
          "You get the solution.",
        ]}
      />

      <section className="bg-card border-y border-border" data-testid="help-types-section">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28">
          <Reveal>
            <h2 className="text-3xl md:text-5xl font-bold text-center" data-testid="help-types-headline">
              Experience we can help you find.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {helpTypes.map((h, i) => (
              <Reveal key={h.testId} delay={(i % 3) * 0.1}>
                <div
                  data-testid={h.testId}
                  className="h-full bg-background border border-border rounded-lg p-8 transition-[border-color,transform] duration-300 hover:border-secondary hover:-translate-y-1"
                >
                  <h.icon size={36} className="text-secondary" aria-hidden="true" />
                  <h3 className="mt-4 text-xl md:text-2xl font-semibold">{h.title}</h3>
                  <p className="mt-3 text-lg text-muted-foreground leading-relaxed">{h.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background" data-testid="need-process-section">
        <div className="max-w-5xl mx-auto px-6 py-20 md:py-28">
          <Reveal>
            <h2 className="text-3xl md:text-5xl font-bold text-center" data-testid="need-process-headline">
              How it works.
            </h2>
          </Reveal>
          <div className="mt-14 space-y-6">
            {steps.map((s, i) => (
              <Reveal key={s.testId} delay={i * 0.06}>
                <div
                  data-testid={s.testId}
                  className="flex items-start gap-6 bg-card border border-border rounded-lg p-8 transition-[border-color] duration-300 hover:border-secondary"
                >
                  <span className="font-serif italic text-5xl md:text-6xl text-secondary leading-none shrink-0 w-16 text-center" aria-hidden="true">
                    {s.n}
                  </span>
                  <div>
                    <h3 className="text-xl md:text-2xl font-semibold">{s.title}</h3>
                    <p className="mt-2 text-lg text-muted-foreground leading-relaxed">{s.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-card border-y border-border" data-testid="need-examples-section">
        <div className="max-w-5xl mx-auto px-6 py-20 md:py-28">
          <Reveal>
            <h2 className="text-3xl md:text-5xl font-bold text-center" data-testid="need-examples-headline">
              What does that look like in real life?
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {examples.map((ex, i) => (
              <Reveal key={ex.testId} delay={(i % 2) * 0.1}>
                <div
                  data-testid={ex.testId}
                  className="h-full bg-background border-l-4 border-secondary rounded-md p-7"
                >
                  <h3 className="text-xl font-bold text-primary">{ex.title}</h3>
                  <p className="mt-3 text-lg text-foreground leading-relaxed">{ex.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-primary text-primary-foreground" data-testid="find-cta-band">
        <div className="max-w-4xl mx-auto px-6 py-20 md:py-28 text-center">
          <Reveal>
            <h2 className="text-3xl md:text-5xl font-bold text-white md:whitespace-nowrap" data-testid="find-cta-headline">
              What are you trying to solve?
            </h2>
            <p className="mt-5 text-lg text-white/85 max-w-2xl mx-auto">
              Tell us the problem. We'll look for the experience that fits.
            </p>
            <button
              onClick={() => scrollToId("request-form")}
              data-testid="find-cta-button"
              className="mt-10 inline-block bg-secondary text-secondary-foreground text-lg font-semibold px-10 py-4 rounded-full transition-[background-color,transform] duration-300 hover:bg-[#caa95c] hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2"
            >
              FIND AN ADVISOR
            </button>
          </Reveal>
        </div>
      </section>

      <section className="bg-background scroll-mt-28" id="request-form" data-testid="request-form-section">
        <div className="max-w-3xl mx-auto px-6 py-20">
          <Reveal>
            <h2 className="text-3xl md:text-4xl font-bold" data-testid="request-form-headline">
              Tell us the problem.
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              You don't need to know what kind of advisor you're looking for. Tell us the problem.
            </p>
            <p
              className="mt-6 inline-block bg-card border-l-4 border-secondary px-5 py-3 text-lg font-semibold text-primary"
              data-testid="find-experience-founding-note"
            >
              Now building our founding local advisor network.
            </p>
          </Reveal>
          <RequestForm onSuccess={() => setDone(true)} />
        </div>
      </section>
    </div>
  );
}
